import React, { useState } from "react";
import ProjectHeader from "../components/ProjectHeader";
import ProjectTabs from "../components/ProjectTabs";
import OverviewTab from "../components/OverviewTab";
import FinancialsTab from "../components/FinancialsTab";
import AssetsTab from "../components/AssetsTab";
import MediaTab from "../components/MediaTab";
import MarketTab from "../components/MarketTab";
import { Button } from "../../../components/ui/button";
import { ArrowLeft, Share2, Info, Home, LogOut } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { useLanguage } from "../../../contexts/LanguageContext";


const UnifiedProjectDetail: React.FC = () => {
    const [activeTab, setActiveTab] = useState("overview");
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const { t } = useLanguage();

    const handleGoBack = () => navigate(-1);
    const handleLogout = async () => {
        await logout();
        navigate("/");
    };

    // Mock data for the "Quantum Solar" preview
    const projectData = {
        title: "Quantum Solar",
        category: "Sustainable Energy Infrastructure",
        price: 1500000,
        currency: "USD",
        roi: 12,
        imageUrl: "https://lh3.googleusercontent.com/aida-public/AB6AXuBH_w_UUmHUWUXV1fhKELDQAm2ZOF13cRFWuN7WDJiNBijGNjNWraSFW2YSSb8ocrUee8W-fQguamqAa5y3g9LfokfzC2JkRACSZAPja4HSxzfPQJW7U9XK5CBLMQXK1H2r4DET0U2MbgeOzY-EPFVAXiE20X-gsjIdP5VF2fD9dWXjdaoeOB6W2Sj-mZbhVvaEcEeQN95sQO9Z9gUCl0cHX-C747V6e8XgpWe0fDjlmoFeDrDaHbjselTsY7BIoff12uBchrsOTQaO",
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case "overview":
                return <OverviewTab />;
            case "financials":
                return <FinancialsTab />;
            case "assets":
                return <AssetsTab />;
            case "media":
                return <MediaTab />;
            case "market":
                return <MarketTab />;
            default:
                return <OverviewTab />;
        }
    };

    return (
        <div className="min-h-screen bg-background pb-20">
            {/* Custom Navigation Bar */}
            <div className="container mx-auto px-4 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                            <img src="/logo.png" alt="Knowvest.plus Logo" className="w-6 h-6 object-contain" />
                        </div>
                        <h1 className="font-bold text-xl">Knowvest.plus</h1>
                    </div>

                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={handleGoBack}>
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            {t("common.backToHome")}
                        </Button>
                        <Button variant="outline" size="sm" asChild>
                            <Link to="/Home">
                                <Home className="w-4 h-4 mr-2" />
                                Home
                            </Link>
                        </Button>

                        {user ? (
                            <Button variant="outline" size="sm" onClick={handleLogout}>
                                <LogOut className="w-4 h-4 mr-2" />
                                Logout
                            </Button>
                        ) : (
                            <Button variant="outline" size="sm" asChild>
                                <Link to="/login">Login</Link>
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {/* Share Control */}
            <div className="fixed top-4 right-4 z-50 flex items-center gap-2">
                <Button
                    variant="ghost"
                    size="icon"
                    className="bg-white/20 backdrop-blur-md rounded-full text-white border border-white/30 hover:bg-white/30"
                >
                    <Share2 className="w-5 h-5" />
                </Button>
            </div>
            <div className="max-w-md mx-auto sm:max-w-xl md:max-w-3xl lg:max-w-6xl px-4">
                <ProjectHeader {...projectData} />

                <ProjectTabs activeTab={activeTab} onTabChange={setActiveTab} />
            </div>
            <main className="max-w-md mx-auto sm:max-w-xl md:max-w-3xl lg:max-w-5xl">
                {renderTabContent()}
            </main>

            {/* Floating Action Bar */}
            <div className="fixed bottom-0 left-0 right-0 p-4 bg-background/80 backdrop-blur-xl border-t border-border z-50">
                <div className="max-w-4xl mx-auto flex items-center gap-3">
                    <div className="hidden sm:block flex-1">
                        <div className="flex items-center gap-2 text-primary font-bold">
                            <Info className="w-4 h-4" />
                            <span className="text-xs uppercase tracking-wider">Due Diligence Ready</span>
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-0.5">Contact broker for information pack.</p>
                    </div>
                    <Button variant="outline" className="flex-1 sm:flex-initial py-6 px-8 rounded-xl font-bold uppercase tracking-widest text-xs border-primary text-primary hover:bg-primary/5 transition-all">
                        Make Offer
                    </Button>
                    <Button className="flex-1 sm:flex-initial py-6 px-10 rounded-xl font-bold uppercase tracking-widest text-xs bg-primary text-white hover:bg-primary/90 shadow-lg shadow-primary/20 transition-all">
                        Buy Now
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default UnifiedProjectDetail;
